import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdVisibility } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import DragDropImageUpload from '../components/DragDropImageUpload';
import GuidelineReviewModal from '../components/GuidelineReviewModal';
import ReaderCheckModal from '../components/ReaderCheckModal';
import { useAuth } from '../context/AuthContext';
import { isAllIndiaState, isInternationalState, statesMatch, ALL_INDIA_STATE, INTERNATIONAL_STATE, displayState } from '../utils/format';
import { reviewArticleWithAI, checkReaderComprehension } from '../api/openai';

function toLocalDatetimeString(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function SearchableSelect({ label, value, options, onChange, placeholder = "Search...", searchKey = "slug" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    (opt.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (opt[searchKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="searchable-select">
        <div className="select-box" onClick={() => setIsOpen(!isOpen)}>
          {(typeof value === 'string' ? displayState(value) : value) || `Select ${label}`}
        </div>
        {isOpen && (
          <div className="select-dropdown">
            <input
              autoFocus
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <div className="options-list">
              {filtered.map(opt => (
                <div
                  key={opt.id || opt.name}
                  className="option-item"
                  onClick={() => {
                    onChange(opt.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{displayState(opt.name)}</span>
                  {opt[searchKey] && <span className="text-muted text-xs">({opt[searchKey]})</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="option-item text-muted">No results found</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PublishArticle() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [form, setForm] = useState({
    title: '', summary: '', source_url: '', source: '', media_type: 'image',
    image_url: '', video_url: '', thumbnail_url: '', images: [],
    category: '', city: '', is_breaking: false, is_featured: false, zone_only: false, breaking_state: ALL_INDIA_STATE,
    published_at: toLocalDatetimeString(new Date()),
    slug: '',
    author_name: '',
    status: 'published',
    manual_thumbnail: false,
    title_en: '', summary_en: '',
    title_hi: '', summary_hi: '',
  });

  const stateMapping = {
    'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
    'తెలంగాణ': 'Telangana',
    'జాతీయం': 'All India',
    'అంతర్జాతీయం': 'International',
    'అంతర్జాతీయ': 'International',
    'International': 'International',
    // Legacy "ఆల్/అల్ ఇండియా" spellings — tolerated for old rows, not written going forward.
    'ఆల్ ఇండియా': 'All India',
    'ఆల్ ఇండియా ': 'All India',
    'అల్ ఇండియా': 'All India',
    'అల్ ఇండియా ': 'All India',
    'ఆంధ్ర ప్రదేశ్': 'Andhra Pradesh',
    'Telangana': 'Telangana',
    'Andhra Pradesh': 'Andhra Pradesh'
  };

  useEffect(() => {
    api.get('/categories').then(res => {
      setCategories(res.data.data);
      const general = res.data.data.find(c => c.name.trim() === 'General');
      if (general) setForm(f => ({ ...f, category: general.name }));
      else if (res.data.data.length > 0) setForm(f => ({ ...f, category: res.data.data[0].name }));
    }).catch(() => { });

    api.get('/cities').then(res => {
      const all = res.data.data;
      setAllCities(all);

      // Filter out null/undefined and trim
      const rawStates = all.map(c => c.state ? c.state.trim() : null).filter(Boolean);
      const uniqueStatesList = [...new Set(rawStates)];

      const stateOptions = uniqueStatesList.map(s => ({
        name: s,
        slug: stateMapping[s] || ''
      }));

      setStates(stateOptions);

      if (uniqueStatesList.length > 0) {
        const initialState = uniqueStatesList[0];
        setSelectedState(initialState);
        const stateCities = all.filter(c => c.state && c.state.trim() === initialState);
        setCities(stateCities);
        if (stateCities.length > 0) setForm(f => ({ ...f, city: stateCities[0].name }));
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (selectedState) {
      const stateName = selectedState.trim();
      const filtered = allCities.filter(c => c.state && (c.state.trim() === stateName || statesMatch(c.state, stateName)));

      setCities(filtered);

      let defaultCity = filtered.length > 0 ? filtered[0].name : '';

      if (stateName === 'ఆంధ్రప్రదేశ్' || stateName === 'Andhra Pradesh') {
        const amaravati = filtered.find(c => c.name.toLowerCase() === 'amaravati' || c.name === 'అమరావతి');
        defaultCity = amaravati ? amaravati.name : (defaultCity || 'Amaravati');
      } else if (stateName === 'తెలంగాణ' || stateName === 'Telangana') {
        const hyderabad = filtered.find(c => c.name.toLowerCase() === 'hyderabad' || c.name === 'హైదరాబాద్');
        defaultCity = hyderabad ? hyderabad.name : (defaultCity || 'Hyderabad');
      } else if (isAllIndiaState(stateName)) {
        const allIndiaCity = filtered.find(c => isAllIndiaState(c.name));
        defaultCity = allIndiaCity ? allIndiaCity.name : ALL_INDIA_STATE + ' ';
      } else if (isInternationalState(stateName)) {
        const intlCity = filtered.find(c => isInternationalState(c.name));
        defaultCity = intlCity ? intlCity.name : INTERNATIONAL_STATE;
      }

      setForm(f => ({ ...f, city: defaultCity }));
    }
  }, [selectedState, allCities]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [showWebPreview, setShowWebPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [cropper, setCropper] = useState({ active: false, image: null, type: null });
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, loading: false, error: null, result: null });
  const [readerCheck, setReaderCheck] = useState({ open: false, loading: false, error: null, questions: [] });

  const handleGalleryFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.data?.url) {
          newUrls.push(res.data.data.url);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setForm(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newUrls]
      }));
      toast.success(`${newUrls.length} gallery image(s) uploaded`);
    }
    setGalleryUploading(false);
  };

  const moveGalleryImage = (index, direction) => {
    setForm(prev => {
      const arr = [...(prev.images || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return prev;
      const temp = arr[index];
      arr[index] = arr[targetIndex];
      arr[targetIndex] = temp;
      return { ...prev, images: arr };
    });
  };

  const removeGalleryImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleGalleryPasteImage = (e) => {
    const items = e.clipboardData?.items || [];
    const filesToUpload = [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          filesToUpload.push(file);
        }
      }
    }
    if (filesToUpload.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      handleGalleryFilesUpload(filesToUpload);
    }
  };

  const wordCount = form.summary.trim().split(/\s+/).filter(Boolean).length;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const validate = () => {
    const errs = {};
    if (form.title.length > 75) errs.title = 'Title cannot exceed 75 characters.';
    if (form.summary.length > 400) errs.summary = 'Summary cannot exceed 400 characters.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';

      // Store reference to prevent garbage collection
      window._tempFileInput = input;

      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const file = files[0];
        if (currentType === 'video') {
          uploadToServer(file, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(file);
        }
      };

      input.click();
      return;
    }

    // Called from DragDropImageUpload with (file, type)
    const file = fileOrType;
    if (type === 'video') {
      uploadToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCropComplete = async (blob) => {
    const type = cropper.type;
    setCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadToServer(file, type);
  };

  const uploadToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') set('image_url', url);
      else if (type === 'extra_image') setForm(prev => ({ ...prev, images: [...(prev.images || []), url] }));
      else if (type === 'thumbnail') {
        set('thumbnail_url', url);
        set('manual_thumbnail', true);
      }
      else if (type === 'video') {
        setForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (form.media_type !== 'image') return;
      if (cropper.active || showWebPreview) return; // Don't intercept if other modals are open

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [form.media_type, cropper.active, showWebPreview]);

  const handlePasteImage = (e) => {
    // Kept for the div's onPaste to prevent any default behavior, though global handler catches it.
    e.preventDefault();
  };

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !form.title) {
      return toast.error('Please enter a title first to generate a slug');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = form.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') set('slug', res.data.data);
        if (type === 'transliterate') {
          return res.data.data; // Return for specific field updates
        }
        toast.success('Slug generated');
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const doPublish = async (overrides = {}) => {
    setLoading(true);
    try {
      const payload = { ...form, state: selectedState, ...overrides };
      // By sending the raw string without 'Z', the backend will parse it
      // using its own local timezone (Asia/Kolkata), thus correctly
      // calculating the UTC time for the database.
      await api.post('/admin/articles', payload);
      toast.success(admin?.role === 'reporter' ? 'Draft saved!' : 'Submitted — running a quick duplicate check, will be live in a few seconds.');
      navigate('/articles');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish');
    } finally {
      setLoading(false);
    }
  };

  const runGuidelineReview = async () => {
    setReviewModal({ open: true, loading: true, error: null, result: null });
    try {
      const result = await reviewArticleWithAI({ title: form.title, summary: form.summary });
      setReviewModal({ open: true, loading: false, error: null, result });
    } catch (err) {
      setReviewModal({ open: true, loading: false, error: err.message || 'AI review failed', result: null });
    }
  };

  const closeReviewModal = () => setReviewModal({ open: false, loading: false, error: null, result: null });

  const runReaderCheck = async () => {
    setReaderCheck({ open: true, loading: true, error: null, questions: [] });
    try {
      const result = await checkReaderComprehension({ title: form.title, summary: form.summary });
      if (result.complete) {
        setReaderCheck({ open: false, loading: false, error: null, questions: [] });
        await runGuidelineReview();
      } else {
        setReaderCheck({ open: true, loading: false, error: null, questions: result.questions });
      }
    } catch (err) {
      setReaderCheck({ open: true, loading: false, error: err.message || 'Reader check failed', questions: [] });
    }
  };

  const closeReaderCheck = () => setReaderCheck({ open: false, loading: false, error: null, questions: [] });

  const handleReaderContinue = async () => {
    closeReaderCheck();
    await runGuidelineReview();
  };

  const handlePublishDirectly = () => {
    closeReaderCheck();
    doPublish();
  };

  const handleUseCorrected = () => {
    const { corrected } = reviewModal.result;
    const cleanTitle = (corrected?.title || '').slice(0, 75);
    const cleanSummary = (corrected?.body || '').slice(0, 400);
    set('title', cleanTitle);
    set('summary', cleanSummary);
    closeReviewModal();
    doPublish({ title: cleanTitle, summary: cleanSummary });
  };

  const handleUseOriginal = () => {
    closeReviewModal();
    doPublish();
  };

  const handlePublishWithoutReview = () => {
    closeReviewModal();
    doPublish();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary) return toast.error('Title and summary required');
    if (form.title.length > 75) return toast.error(`Title exceeds limit by ${form.title.length - 75} characters`);
    if (form.summary.length > 400) return toast.error(`Summary exceeds limit by ${form.summary.length - 400} characters`);
    if (!validate()) return toast.error('Please fix validation errors');

    // Check if AI Check is disabled in Admin Settings
    try {
      const res = await api.get('/admin/settings/ai-check');
      if (res.data?.success && res.data.data?.enabled === false) {
        // AI check is OFF: skip AI check and publish directly through existing flow
        return doPublish();
      }
    } catch (err) {
      // If error fetching setting, continue with default flow
    }

    await runReaderCheck();
  };

  const handleTransliterate = async (field) => {
    const text = form[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      set(field, result.slice(0, limit));
    }
  };

  const handleGenerateTranslations = async () => {
    if (!form.title || !form.summary) {
      return toast.error('Please enter Telugu title and summary first');
    }

    setLoading(true);
    let successCount = 0;

    try {
      // English Translation
      const enRes = await api.post('/admin/ai/generate', { type: 'translate', title: form.title, summary: form.summary, targetLanguage: 'English' });

      if (enRes.data.success && enRes.data.data.headline && enRes.data.data.body) {
        set('title_en', enRes.data.data.headline.slice(0, 150)); // allow a bit more for English
        set('summary_en', enRes.data.data.body.slice(0, 600));
        successCount++;
      } else {
        toast.error('English translation failed');
      }
    } catch (err) {
      toast.error('English translation error');
    }

    try {
      // Hindi Translation
      const hiRes = await api.post('/admin/ai/generate', { type: 'translate', title: form.title, summary: form.summary, targetLanguage: 'Hindi' });

      if (hiRes.data.success && hiRes.data.data.headline && hiRes.data.data.body) {
        set('title_hi', hiRes.data.data.headline.slice(0, 150));
        set('summary_hi', hiRes.data.data.body.slice(0, 600));
        successCount++;
      } else {
        toast.error('Hindi translation failed');
      }
    } catch (err) {
      toast.error('Hindi translation error');
    }

    setLoading(false);
    if (successCount > 0) toast.success('Translations generated successfully!');
  };

  const handleCopySingleTranslation = async (lang, title, summary) => {
    try {
      const reviewPrompt = "Please review this news as a journalist and an experienced news writer. Improve the content wherever necessary by correcting grammar, punctuation, sentence structure, clarity, factual consistency, and readability while following professional news editorial style guidelines. Preserve the original meaning and tone. Return only the revised headline and article.\n\n";
      const copyText = `${reviewPrompt}${lang} Headline:\n${title || ''}\n\n${lang} Article:\n${summary || ''}`;

      await navigator.clipboard.writeText(copyText);
      toast.success(`${lang} translation copied successfully!`);
    } catch (err) {
      toast.error(`Failed to copy ${lang.toLowerCase()} translation.`);
    }
  };

  return (
    <div className="page" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
      <div className="publish-layout" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Create New Article</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              className="pub-inline-select"
              value={form.status}
              onChange={e => set('status', e.target.value)}
              disabled={admin?.role === 'reporter'}
              style={{ fontSize: '13px', padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card)', color: 'var(--text)', cursor: 'pointer' }}
            >
              <option value="draft">📋 Draft</option>
              <option value="published">✓ Published</option>
            </select>
            <button
              type="button"
              className="btn btn-outline-red"
              onClick={() => setShowWebPreview(true)}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              👁️ Preview
            </button>
            <button
              type="submit"
              form="publish-form"
              className="btn btn-primary"
              disabled={loading || checkingDuplicate || readerCheck.loading || reviewModal.loading || form.title.length > 75 || form.summary.length > 400}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              {checkingDuplicate ? '🔍 Checking duplicates...' : readerCheck.loading ? '📖 Reading...' : reviewModal.loading ? '🤖 Reviewing...' : loading ? '⏳ Publishing...' : '✓ Publish'}
            </button>
          </div>
        </div>

        <form id="publish-form" className="publish-form" onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'flex-start', maxWidth: '100%' }}>
          <div className="pub-panel" style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <div className="form-group">
              <div className="flex-row justify-between items-center mb-1">
                <label className="mb-0">Title</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: form.title.length > 75 ? '#DC2626' : '#6B7280',
                    transition: 'color 0.2s'
                  }}>
                    {form.title.length} / 75
                  </span>
                  {form.title.length > 75 && (
                    <span style={{
                      fontSize: '12px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '400',
                      animation: 'pulse 1s infinite'
                    }}>
                      +{form.title.length - 75} extra
                    </span>
                  )}
                </div>
              </div>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Type in English (e.g. namaskaram)..."
                style={{
                  borderColor: form.title.length > 75 ? '#DC2626' : undefined,
                  backgroundColor: form.title.length > 75 ? '#FEF2F2' : undefined
                }}
                required
              />
              {form.title.length > 75 && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px', display: 'block' }}>
                  ⚠️ Title exceeds limit by {form.title.length - 75} characters. Please trim it down to 75 characters max.
                </span>
              )}
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>

            {/* <div className="form-group">
            <div className="flex-row justify-between items-center mb-1">
              <label className="mb-0">URL Slug (English only)</label>
              <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate('slug')} disabled={loading}>
                ✨ AI Generate
              </button>
            </div>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => set('slug', e.target.value)}
              placeholder="e.g. telangana-politics (auto-generated if empty)"
            />
          </div> */}

            <div className="form-group">
              <div className="flex-row justify-between items-center mb-1">
                <label className="mb-0">Summary</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: form.summary.length > 400 ? '#DC2626' : '#6B7280',
                    transition: 'color 0.2s'
                  }}>
                    {form.summary.length} / 400
                  </span>
                  {form.summary.length > 400 && (
                    <span style={{
                      fontSize: '12px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '400',
                      animation: 'pulse 1s infinite'
                    }}>
                      +{form.summary.length - 400} extra
                    </span>
                  )}
                </div>
              </div>
              <textarea
                value={form.summary}
                onChange={(e) => set('summary', e.target.value)}
                placeholder="Type in English..."
                rows={7}
                style={{
                  borderColor: form.summary.length > 400 ? '#DC2626' : undefined,
                  backgroundColor: form.summary.length > 400 ? '#FEF2F2' : undefined,
                  resize: 'vertical'
                }}
                required
              />
              {form.summary.length > 400 && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px', display: 'block' }}>
                  ⚠️ Summary exceeds limit by {form.summary.length - 400} characters. Please trim it down to 400 characters max.
                </span>
              )}
              {errors.summary && <span className="field-error">{errors.summary}</span>}
            </div>


            {(form.title_en || form.summary_en) && (
              <div className="form-group" style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: 'var(--text-secondary)' }}>English Translation</h4>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => handleCopySingleTranslation('English', form.title_en, form.summary_en)}
                    style={{ border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <div className="form-group mb-sm">
                  <label className="mb-0">Title (English)</label>
                  <input
                    type="text"
                    value={form.title_en}
                    onChange={(e) => set('title_en', e.target.value)}
                    placeholder="English title..."
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-0">Summary (English)</label>
                  <textarea
                    value={form.summary_en}
                    onChange={(e) => set('summary_en', e.target.value)}
                    placeholder="English summary..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {(form.title_hi || form.summary_hi) && (
              <div className="form-group" style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: 'var(--text-secondary)' }}>Hindi Translation</h4>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => handleCopySingleTranslation('Hindi', form.title_hi, form.summary_hi)}
                    style={{ border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <div className="form-group mb-sm">
                  <label className="mb-0">Title (Hindi)</label>
                  <input
                    type="text"
                    value={form.title_hi}
                    onChange={(e) => set('title_hi', e.target.value)}
                    placeholder="Hindi title..."
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-0">Summary (Hindi)</label>
                  <textarea
                    value={form.summary_hi}
                    onChange={(e) => set('summary_hi', e.target.value)}
                    placeholder="Hindi summary..."
                    rows={4}
                  />
                </div>
              </div>
            )}


            {/* AUTHOR NAME (Restored) */}
            {/* <div className="form-group">
            <div className="flex-row justify-between items-center mb-1">
              <label className="mb-0">Author Name</label>
              <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate('author')} disabled={loading}>
                ✨ AI Random Name
              </button>
            </div>
            <input
              type="text"
              value={form.author_name}
              onChange={(e) => set('author_name', e.target.value)}
              placeholder="e.g. Rajesh Kumar..."
            />
          </div> */}

            <div className="form-group" style={{ display: 'none' }}>
              <label>Publish Date & Time</label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => set('published_at', e.target.value)}
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ margin: 0 }}>Media Type</label>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>720 × 590 px</span>
              </div>
              <div className="radio-group">
                <label className={`radio-card ${form.media_type === 'image' ? 'selected' : ''}`}>
                  <input type="radio" value="image" checked={form.media_type === 'image'}
                    onChange={() => set('media_type', 'image')} />
                  Image
                </label>
                <label className={`radio-card ${form.media_type === 'video' ? 'selected' : ''}`}>
                  <input type="radio" value="video" checked={form.media_type === 'video'}
                    onChange={() => set('media_type', 'video')} />
                  Video
                </label>
              </div>
            </div>

            {form.media_type === 'image' && (
              <>
                <div className="form-group">
                  <label>Article Image (Web & App)</label>
                  <div className="upload-area" tabIndex={0} onClick={() => handleFileSelect('image')} onPaste={handlePasteImage}>
                    {form.image_url ? (
                      <img src={form.image_url} alt="preview" className="upload-preview" />
                    ) : (
                      <p>{uploading ? 'Processing...' : 'Click to select & crop — or paste an image (Ctrl+V)'}</p>
                    )}
                  </div>
                  <input type="text" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="Or paste image URL" className="mt-sm" style={{ display: 'none' }} />
                </div>

                {/* Gallery Images Section */}
                <div className="form-group" style={{ marginTop: '20px' }} tabIndex={0} onPaste={handleGalleryPasteImage}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ margin: 0, fontWeight: 700 }}>Gallery Images</label>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Click, drag & drop, or paste (Ctrl+V)</span>
                  </div>
                  <div
                    className="upload-area"
                    tabIndex={0}
                    onPaste={handleGalleryPasteImage}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                      input.onchange = (e) => handleGalleryFilesUpload(Array.from(e.target.files));
                      input.click();
                    }}
                    style={{ cursor: 'pointer', padding: '24px 16px', border: '2px dashed #CBD5E1', borderRadius: '10px', textAlign: 'center', backgroundColor: 'var(--bg)' }}
                  >
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {galleryUploading ? 'Uploading gallery images...' : 'Click to select multiple images, drag & drop, or paste (Ctrl+V)'}
                    </p>
                  </div>

                  {/* Gallery Thumbnails Grid & Plus Card */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    {(form.images || []).map((imgUrl, index) => (
                      <div key={index} style={{ position: 'relative', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden', backgroundColor: 'var(--card)', height: '108px', display: 'flex', flexDirection: 'column' }}>
                        <img src={typeof imgUrl === 'string' ? imgUrl : imgUrl.image_url || imgUrl.url} alt={`gallery-${index}`} style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', height: '28px', boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button
                              type="button"
                              disabled={index === 0}
                              onClick={(e) => { e.stopPropagation(); moveGalleryImage(index, -1); }}
                              style={{ border: 'none', background: 'transparent', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                              title="Move Left"
                            >
                              ◀
                            </button>
                            <button
                              type="button"
                              disabled={index === (form.images || []).length - 1}
                              onClick={(e) => { e.stopPropagation(); moveGalleryImage(index, 1); }}
                              style={{ border: 'none', background: 'transparent', cursor: index === (form.images || []).length - 1 ? 'default' : 'pointer', opacity: index === (form.images || []).length - 1 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                              title="Move Right"
                            >
                              ▶
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeGalleryImage(index); }}
                            style={{ border: 'none', background: 'transparent', color: '#EF233C', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', padding: '2px 4px' }}
                            title="Remove Image"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add Images (+) Card */}
                    <div
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                        input.onchange = (e) => handleGalleryFilesUpload(Array.from(e.target.files));
                        input.click();
                      }}
                      style={{
                        height: '108px',
                        border: '2px dashed #CBD5E1',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: 'var(--bg)',
                        transition: 'all 0.2s ease',
                        color: 'var(--text-secondary)',
                        boxSizing: 'border-box'
                      }}
                      title="Add Images"
                    >
                      <span style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>Add Images</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {form.media_type === 'video' && (
              <>
                <div className="form-group">
                  <label>Video URL</label>
                  <input type="text" value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="MP4 URL..." />
                  <button type="button" className="btn btn-xs mt-xs" onClick={() => handleFileSelect('video')}>Upload Video</button>
                </div>
                <div className="form-group">
                  <label>Video Thumbnail (App & Web)</label>
                  <DragDropImageUpload
                    type="thumbnail"
                    uploading={uploading}
                    imageUrl={form.thumbnail_url}
                    onFileSelect={handleFileSelect}
                  />
                  <input type="text" value={form.thumbnail_url} onChange={(e) => set('thumbnail_url', e.target.value)}
                    placeholder="Or paste thumbnail URL" className="mt-sm" style={{ display: 'none' }} />
                </div>
              </>
            )}
          </div>

          <div className="pub-panel" style={{ display: 'flex', flexDirection: 'column', gap: '20px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <div className="location-picker">
              <div className="location-picker-row">
                <span className="location-picker-label">Category</span>
                <div className="pill-radio-row">
                  {categories.map(cat => {
                    const isSelected = form.category && form.category.trim() === cat.name.trim();
                    return (
                      <label key={cat.id} className={`pill-radio${isSelected ? ' selected' : ''}`}>
                        <input
                          type="radio"
                          name="quick_category"
                          checked={isSelected}
                          onChange={() => {
                            set('category', cat.name);
                            // Only "General" is location-specific — every other category
                            // (క్రీడలు, బిజినెస్, వినోదం, ...) doesn't need a sub category/city,
                            // so pin it to the జాతీయం placeholder (the backend enforces this
                            // too on save, this just keeps the UI consistent).
                            if (cat.name.trim() !== 'General') {
                              setSelectedState(ALL_INDIA_STATE + ' ');
                              set('city', ALL_INDIA_STATE);
                            }
                          }}
                        />
                        {cat.name}
                      </label>
                    );
                  })}
                </div>
              </div>

              {form.category && form.category.trim() === 'General' && (
                <>
                  <div className="location-picker-row">
                    <span className="location-picker-label">Sub Category</span>
                    <div className="pill-radio-row">
                      {['ఆంధ్రప్రదేశ్', 'తెలంగాణ', ALL_INDIA_STATE + ' ', INTERNATIONAL_STATE].map(stateName => {
                        const isSelected = selectedState && statesMatch(selectedState, stateName);
                        return (
                          <label key={stateName} className={`pill-radio${isSelected ? ' selected' : ''}`}>
                            <input
                              type="radio"
                              name="quick_state"
                              checked={isSelected}
                              onChange={() => setSelectedState(stateName)}
                            />
                            {stateName.trim()}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="location-picker-row">
                    <span className="location-picker-label">City</span>
                    <SearchableSelect
                      label=""
                      value={form.city}
                      options={cities}
                      onChange={(val) => set('city', val)}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="form-group flex-row gap-8" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.is_featured}
                    onChange={(e) => set('is_featured', e.target.checked)} />
                  <span className="featured-tag">Featured Article</span>
                </label>
              </div>
              <div style={{ flex: 1 }}>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.zone_only}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      set('zone_only', checked);
                      if (checked) {
                        set('is_breaking', false);
                      }
                    }} />
                  <span className="zone-tag" style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Show only in zone</span>
                </label>
              </div>
            </div>

            {!form.zone_only ? (
              <>
                <div className="form-group flex-row gap-8" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <label className="checkbox-label">
                      <input type="checkbox" checked={form.is_breaking}
                        onChange={(e) => set('is_breaking', e.target.checked)} />
                      <span className="breaking-tag">Breaking News</span>
                    </label>
                  </div>
                </div>

                {form.is_breaking && (
                  <div className="location-picker-row">
                    <span className="location-picker-label">Show Breaking News For</span>
                    <div className="pill-radio-row">
                      {['తెలంగాణ', 'ఆంధ్రప్రదేశ్', ALL_INDIA_STATE].map(target => {
                        const isSelected = (form.breaking_state || ALL_INDIA_STATE).trim() === target.trim();
                        return (
                          <label key={target} className={`pill-radio${isSelected ? ' selected' : ''}`}>
                            <input
                              type="radio"
                              name="breaking_state"
                              checked={isSelected}
                              onChange={() => set('breaking_state', target)}
                            />
                            {target === ALL_INDIA_STATE ? 'All (Telangana + Andhra Pradesh)' : target}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block' }}>
                  This article will only appear in the {form.city || 'selected city'} feed — it will be excluded from the main/national feed.
                </span>

                <div className="location-picker-row" style={{ margin: 0 }}>
                  <span className="location-picker-label" style={{ fontWeight: 600, color: 'var(--text)', fontSize: '13px' }}>
                    Breaking News for this Zone?
                  </span>
                  <div className="pill-radio-row">
                    <label className={`pill-radio${!form.is_breaking ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name="zone_breaking_news"
                        checked={!form.is_breaking}
                        onChange={() => set('is_breaking', false)}
                      />
                      No
                    </label>
                    <label
                      className={`pill-radio${form.is_breaking ? ' selected' : ''}`}
                      style={form.is_breaking ? { background: '#DC2626', color: '#fff', borderColor: '#DC2626' } : {}}
                    >
                      <input
                        type="radio"
                        name="zone_breaking_news"
                        checked={form.is_breaking}
                        onChange={() => set('is_breaking', true)}
                      />
                      Yes
                    </label>
                  </div>
                </div>
              </div>
            )}

            {form.is_breaking && (
              <div className="form-group" style={{ marginTop: '10px' }}>
                <label>Custom Notification Title (Optional)</label>
                <input
                  type="text"
                  value={form.custom_notification_title || ''}
                  onChange={(e) => set('custom_notification_title', e.target.value)}
                  placeholder="Example: Heavy Rain Alert in Hyderabad"
                />
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                  Leave blank to use the original article title.
                </span>
              </div>
            )}
          </div>
        </form>

        <div className="publish-sidebar"></div>
      </div>


      {showWebPreview && (
        <WebPreviewModal
          article={form}
          onClose={() => setShowWebPreview(false)}
        />
      )}

      {cropper.active && (
        <ImageCropper
          image={cropper.image}
          onCropComplete={handleCropComplete}
          onCancel={() => setCropper({ active: false, image: null, type: null })}
        />
      )}

      <ReaderCheckModal
        open={readerCheck.open}
        loading={readerCheck.loading}
        error={readerCheck.error}
        questions={readerCheck.questions}
        onContinue={handleReaderContinue}
        onGoBack={closeReaderCheck}
        onRetry={runReaderCheck}
        onPublishDirectly={handlePublishDirectly}
      />

      <GuidelineReviewModal
        open={reviewModal.open}
        loading={reviewModal.loading}
        error={reviewModal.error}
        result={reviewModal.result}
        original={{ title: form.title, summary: form.summary }}
        onUseCorrected={handleUseCorrected}
        onUseOriginal={handleUseOriginal}
        onPublishWithoutReview={handlePublishWithoutReview}
        onRetry={runGuidelineReview}
        onClose={closeReviewModal}
      />

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This looks very similar to an already-published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before publishing.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Similarity Score
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.score}%
                    </span>
                  </div>

                  <div className="dup-title" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Article
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn btn-ghost"
                onClick={() => setDuplicateWarning(null)}
              >
                Edit Article
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  setDuplicateWarning(null);
                  doPublish();
                }}
                disabled={loading}
              >
                {loading ? 'Publishing...' : 'Publish Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Matched Duplicate Article Preview Modal */}
      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, borderRadius: 16, overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: 'var(--bg)', padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
                Matched Duplicate Article Details (ID: #{viewMatchedArticle.id})
              </h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>

            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFF1F2', border: '1px solid #FECDD3', padding: '10px 14px', borderRadius: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#881337' }}>Similarity Score</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#E11D48' }}>{viewMatchedArticle.score}% Match</span>
              </div>

              {(viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url) && (
                <img
                  src={viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url}
                  alt=""
                  style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }}
                />
              )}

              <h4 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4 }}>
                {viewMatchedArticle.title}
              </h4>

              {viewMatchedArticle.summary && (
                <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, marginBottom: 16, background: 'var(--bg)', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
                  {viewMatchedArticle.summary}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg)', padding: 12, borderRadius: 10 }}>
                <div><strong>Article ID:</strong> #{viewMatchedArticle.id}</div>
                <div><strong>Reporter:</strong> {viewMatchedArticle.reporter_name || 'Admin'}</div>
                <div><strong>Category:</strong> {viewMatchedArticle.category || 'General'}</div>
                <div><strong>Published Date:</strong> {new Date(viewMatchedArticle.published_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '14px 20px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {viewMatchedArticle.slug ? (
                <a
                  href={`http://localhost:3000/${viewMatchedArticle.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                  style={{ borderColor: '#0284C7', color: '#0284C7', fontSize: 13 }}
                >
                  Open Web Article ↗
                </a>
              ) : <div />}
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
